import {
  createContext,
  useContext,
  type ReactElement,
  type ReactNode,
  type KeyboardEvent,
} from 'react';
import { cn } from '@/lib/utils';

interface TabsContextValue {
  readonly activeTab: string;
  readonly onTabChange: (tab: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (ctx === null) throw new Error('Tabs subcomponent used outside <Tabs>');
  return ctx;
}

interface TabsProps {
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly children: ReactNode;
  readonly className?: string;
}

export function Tabs({ value, onValueChange, children, className }: TabsProps): ReactElement {
  return (
    <TabsContext.Provider value={{ activeTab: value, onTabChange: onValueChange }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

interface TabsListProps {
  readonly children: ReactNode;
  readonly className?: string;
  readonly 'aria-label'?: string;
}

export function TabsList({
  children,
  className,
  'aria-label': ariaLabel,
}: TabsListProps): ReactElement {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'flex items-end border-b border-border bg-card px-6 gap-0',
        className,
      )}
    >
      {children}
    </div>
  );
}

interface TabsTriggerProps {
  readonly value: string;
  readonly children: ReactNode;
  readonly icon?: ReactNode;
  readonly className?: string;
}

function focusTabAndActivate(
  tab: HTMLButtonElement,
  onTabChange: (tab: string) => void,
): void {
  const nextValue = tab.dataset.tabValue;
  if (nextValue === undefined) return;
  onTabChange(nextValue);
  requestAnimationFrame(() => {
    tab.focus();
  });
}

export function TabsTrigger({
  value,
  children,
  icon,
  className,
}: TabsTriggerProps): ReactElement {
  const { activeTab, onTabChange } = useTabsContext();
  const isActive = activeTab === value;

  function handleKeyDown(e: KeyboardEvent<HTMLButtonElement>): void {
    const tablist = e.currentTarget.closest('[role="tablist"]');
    if (tablist === null) return;

    const tabs = Array.from(
      tablist.querySelectorAll<HTMLButtonElement>('[role="tab"]'),
    );
    const index = tabs.indexOf(e.currentTarget);
    if (index < 0) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onTabChange(value);
      return;
    }

    // Horizontal tablist: WAI-ARIA uses Left/Right only (avoid stealing Up/Down for focus moves).
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = tabs[(index + 1) % tabs.length];
      if (next !== undefined) focusTabAndActivate(next, onTabChange);
      return;
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const next = tabs[(index - 1 + tabs.length) % tabs.length];
      if (next !== undefined) focusTabAndActivate(next, onTabChange);
      return;
    }

    if (e.key === 'Home') {
      e.preventDefault();
      const first = tabs[0];
      if (first !== undefined) focusTabAndActivate(first, onTabChange);
      return;
    }

    if (e.key === 'End') {
      e.preventDefault();
      const last = tabs[tabs.length - 1];
      if (last !== undefined) focusTabAndActivate(last, onTabChange);
    }
  }

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      data-tab-value={value}
      onClick={() => {
        onTabChange(value);
      }}
      onKeyDown={handleKeyDown}
      className={cn(
        'flex items-center gap-1.5 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap',
        'border-b-2 -mb-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
        isActive
          ? 'border-primary text-primary'
          : 'border-transparent text-muted-foreground hover:text-card-foreground hover:border-muted-foreground/40',
        className,
      )}
    >
      {icon !== undefined && icon}
      {children}
    </button>
  );
}

interface TabsContentProps {
  readonly value: string;
  readonly children: ReactNode;
  readonly className?: string;
}

export function TabsContent({
  value,
  children,
  className,
}: TabsContentProps): ReactElement | null {
  const { activeTab } = useTabsContext();
  if (activeTab !== value) return null;

  return (
    <div role="tabpanel" className={cn('outline-none', className)}>
      {children}
    </div>
  );
}
